import { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Button, MenuItem, Stack, TextField, CircularProgress } from '@mui/material';
import { FeedbackContext } from '../../../App';
import EntityDetailPopup from '../../utility/EntityDetailPopup';
import apiService from '../../../api/apiService';
import {
  cropClasses,
  DownArrowIcon,
  miscClasses,
  popupClasses,
} from '../../utility';

export default function AttachPhasePlanPopup(props) {
  const { open, onClose, cropplanid, currDarkThemeClass, onSuccess, phaseplans = [] } = props;
  const feedbackCX = useContext(FeedbackContext);

  const [values, setValues] = useState({
    phaseplan: '',
    loading: false,
  });

  const handleSetValues = useCallback(
    (prop, value) => setValues(state => ({ ...state, [prop]: value })),
    []
  );

  // Reset on open
  useEffect(() => {
    if (open) {
      setValues({ phaseplan: '', loading: false });
    }
  }, [open]);

  const cropPlanMenuItemProps = {
    className: `${cropClasses?.menuItem} ${currDarkThemeClass}`,
    focusVisibleClassName: miscClasses?.menuItemFocusDark,
  };

  const phasePlanMenuItems =
    phaseplans?.length > 0
      ? phaseplans.map((p, i) => (
          <MenuItem key={i} value={p.id} {...cropPlanMenuItemProps}>
            {p.name} ({p.total_days} Days)
          </MenuItem>
        ))
      : [
          <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
            No Available Phase Plans
          </MenuItem>,
        ];

  const textFieldProps = ({ prop, label, ...rest }) => ({
    autoComplete: 'off',
    className: `${popupClasses?.strainEntry?.textField ?? ''} ${currDarkThemeClass}`,
    fullWidth: true,
    label,
    value: values[prop] ?? '',
    onChange: ({ target: { value } }) => handleSetValues(prop, value),
    ...rest,
  });

  const canAttach = values.phaseplan && !values.loading;

  const handleAttach = async () => {
    if (!canAttach) return;
    handleSetValues('loading', true);

    try {
      const response = await apiService.createInstance('v2/cropplan/attach-phaseplan/', {
        cropplan: cropplanid,
        phaseplan: values.phaseplan,
      });

      if (response.status === 200 || response.status === 201) {
        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Phase Plan attached successfully.',
          autoHideDuration: 3000,
        });
        onSuccess?.();
        onClose?.();
      }
    } catch (error) {
      console.error('Error attaching phase plan:', error);
      const errMsg =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        'Error attaching Phase Plan. Please try again.';
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: errMsg,
        autoHideDuration: 4000,
      });
    } finally {
      handleSetValues('loading', false);
    }
  };

  const entityDetailPopupProps = {
    open,
    onClose,
    currDarkThemeClass,
    title: 'Attach Phase Plan',
    headContentText: 'Select a Phase Plan to set up phases for this Crop Run.',
    modalMaxWidth: '40vw',
    toShowSave: false,
    toShowCreate: false,
  };

  return (
    <EntityDetailPopup {...entityDetailPopupProps}>
      <Stack spacing={3} sx={{ mt: 1 }}>
        {/* Phase Plan selector */}
        <TextField
          {...textFieldProps({ prop: 'phaseplan', label: 'Phase Plan' })}
          select
          SelectProps={{ IconComponent: DownArrowIcon }}
        >
          {phasePlanMenuItems}
        </TextField>

        {/* Attach button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
          <Button
            variant="contained"
            disabled={!canAttach}
            onClick={handleAttach}
            startIcon={values.loading && <CircularProgress size={16} />}
            sx={{
              background: 'linear-gradient(135deg, #B1D23A 0%, #7EA82A 100%)',
              color: '#000',
              fontWeight: 700,
              '&:hover': { background: 'linear-gradient(135deg, #C5E44A 0%, #8EBB30 100%)' },
              '&:disabled': { opacity: 0.5 },
            }}
          >
            {values.loading ? 'Attaching...' : 'Attach Phase Plan'}
          </Button>
        </Box>
      </Stack>
    </EntityDetailPopup>
  );
}
